// src/components/NewVehicleDealers.tsx

import { Box, Typography, Button, Paper, Chip, Avatar, Divider } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PhoneIcon from '@mui/icons-material/Phone';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { NewVehicle } from '../types/NewVehicleTypes';

interface NewVehicleDealersProps {
  vehicle: NewVehicle;
  currentPrice?: number | null;
}

export default function NewVehicleDealers({ vehicle, currentPrice }: NewVehicleDealersProps) {
  const mainDealer = vehicle.vehicleDealerInfo;
  const otherDealers = vehicle.dealerContacts || [];

  if (!mainDealer && otherDealers.length === 0) return null;

  return (
    <Box mb={4}>
      {/* Header section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" fontWeight={800}>Dealers</Typography>
        <Button 
          startIcon={<CompareArrowsIcon />} 
          color="inherit" 
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
        >
          Compare Dealers
        </Button>
      </Box>

      {/* List section */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        
        {/* ── PRIMARY DEALER ── */}
        {mainDealer && (
          <Box 
            display="flex" 
            flexDirection={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            justifyContent="space-between" 
            p={2.5} 
            gap={2}
          >
            {/* Logo & Name */}
            <Box display="flex" alignItems="center" gap={2} flex={1}>
              <Avatar 
                src={mainDealer.imageUrl || undefined} 
                alt={mainDealer.dealerName} 
                variant="rounded"
                sx={{ width: 60, height: 60, bgcolor: 'transparent', '& img': { objectFit: 'contain' } }}
              >
                {!mainDealer.imageUrl && mainDealer.dealerName?.charAt(0)}
              </Avatar>
              <Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight={700}>{mainDealer.dealerName}</Typography>
                  <Chip label="Best Price" size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700, fontSize: 11, height: 22, borderRadius: 1 }} />
                </Box>
              </Box>
            </Box>

            {/* Price & Stock Status */}
            <Box textAlign={{ xs: 'left', sm: 'center' }} minWidth={120}>
              <Typography variant="h6" fontWeight={800}>
                {currentPrice ? `$${currentPrice.toLocaleString()}` : 'POA'}
              </Typography>
              <Chip 
                label="Ready Stock" 
                size="small" 
                sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700, fontSize: 11, height: 22, borderRadius: 1, mt: 0.5 }} 
              />
            </Box>

            {/* Action Buttons */}
            <Box display="flex" flexDirection="column" gap={1} minWidth={140} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={700}>{mainDealer.contactNumber}</Typography>
              </Box>
              <Button 
                variant="contained" 
                fullWidth
                sx={{ bgcolor: '#FFB800', color: '#000', fontWeight: 700, textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#e5a600' } }}
              >
                View Details
              </Button>
            </Box>
          </Box>
        )}

        {/* ── OTHER DISTRIBUTORS ── */}
        {otherDealers.length > 0 && (
          <>
            {mainDealer && <Divider />}
            <Box sx={{ bgcolor: '#f9f9f9', px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>
                Other Authorized Distributors
              </Typography>
            </Box>
            
            {otherDealers.map((dealer, idx) => (
              <Box key={dealer.newVehicleDealerId || idx}>
                <Box 
                  display="flex" 
                  flexDirection={{ xs: 'column', sm: 'row' }} 
                  alignItems={{ xs: 'flex-start', sm: 'center' }} 
                  justifyContent="space-between" 
                  p={2.5} 
                  gap={2}
                >
                  
                  {/* Icon & Details */}
                  <Box display="flex" alignItems="flex-start" gap={2} flex={1}>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight={700}>{dealer.dealerName}</Typography>
                        {dealer.availabilityStatus && (
                          <Chip label={dealer.availabilityStatus} size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700, fontSize: 10, height: 20, borderRadius: 1 }} />
                        )}
                      </Box>
                      
                      {dealer.contactPerson && (
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5} color="text.secondary">
                          <PersonOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontSize: 13 }}>{dealer.contactPerson}</Typography>
                        </Box>
                      )}
                      
                      {dealer.branchAddress && (
                        <Box display="flex" alignItems="flex-start" gap={0.5} mt={0.5} color="text.secondary">
                          <LocationOnOutlinedIcon sx={{ fontSize: 16, mt: 0.2 }} />
                          <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.4 }}>{dealer.branchAddress}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box display="flex" flexDirection="column" gap={1} minWidth={140} alignItems={{ xs: 'flex-start', sm: 'flex-end' }} mt={{ xs: 1, sm: 0 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight={700}>{dealer.contactNumber}</Typography>
                    </Box>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      sx={{ color: '#000', borderColor: '#e0e0e0', fontWeight: 700, textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: 'grey.50', borderColor: '#bdbdbd' } }}
                    >
                      Contact Dealer
                    </Button>
                  </Box>

                </Box>
                {idx < otherDealers.length - 1 && <Divider />}
              </Box>
            ))}
          </>
        )}
      </Paper>
    </Box>
  );
}